/**
 * Feature Flags Configuration
 *
 * Environment-based feature toggling system.
 * Use this for features that should be enabled/disabled per environment.
 * Optionally supports language restrictions per feature.
 *
 * Note: This is different from GrowthBook which is used for A/B testing experiments.
 * Use FeatureFlags for environment-based toggles, GrowthBook for user experiments.
 */

export enum FeatureFlag {
	BLOG = 'blog',
}

type Environment = 'prod' | 'acpt' | 'int' | 'dev'

interface FeatureFlagValue {
	enabled: boolean
	/** If provided, feature is only available for these languages. If undefined, available for all. */
	languages?: string[]
}

type FeatureFlagConfig = Record<FeatureFlag, FeatureFlagValue>

const featureFlagConfig: Record<Environment, FeatureFlagConfig> = {
	prod: {
		[FeatureFlag.BLOG]: { enabled: false, languages: ['en'] },
	},
	acpt: {
		[FeatureFlag.BLOG]: { enabled: true, languages: ['en'] },
	},
	int: {
		[FeatureFlag.BLOG]: { enabled: true, languages: ['en'] },
	},
	dev: {
		[FeatureFlag.BLOG]: { enabled: true, languages: ['en'] },
	},
}

/**
 * Get the current environment from NEXT_PUBLIC_SENTRY_ENV
 * Falls back to 'dev' if not set
 */
export const getCurrentEnvironment = (): Environment => {
	const env = process.env.NEXT_PUBLIC_SENTRY_ENV as Environment
	return env || 'dev'
}

/**
 * Check if a feature flag is enabled for the current environment and locale
 * @param flag - The feature flag to check
 * @param locale - Optional locale to check against language restrictions
 */
export const isFeatureEnabled = (flag: FeatureFlag, locale?: string): boolean => {
	const env = getCurrentEnvironment()
	const flagConfig = featureFlagConfig[env]?.[flag]

	if (!flagConfig) return false
	if (!flagConfig.enabled) return false

	// If languages are specified and locale is provided, check if locale is allowed
	if (flagConfig.languages && locale) {
		return flagConfig.languages.includes(locale)
	}

	// If no languages specified, feature is available for all locales
	// If no locale provided, only check environment-level enabled status
	return flagConfig.enabled
}

/**
 * Get all feature flags for the current environment
 */
export const getFeatureFlags = (): FeatureFlagConfig => {
	const env = getCurrentEnvironment()
	return featureFlagConfig[env] ?? featureFlagConfig.dev
}

/**
 * Get supported languages for a feature flag
 */
export const getFeatureSupportedLanguages = (
	flag: FeatureFlag
): string[] | undefined => {
	const env = getCurrentEnvironment()
	return featureFlagConfig[env]?.[flag]?.languages
}

